import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { toast } from "helpers/toast";
import { useTranslation } from "translation/useTranslation";
import ErrorAlert from "components/ui/ErrorAlert";
import EmployeeSelector from "./EmployeeSelector";
import Link from "components/ui/Link";
import ActionChangePreview from "./ActionChangePreview";
import { applySelectionChange, buildWhoList } from "./actionChangePreviewHelpers";
import { Button } from "components/ui";

export default function AssignPeriodAction({ action, page, setPage, filterCount, setFilterCount }) {
  const navigate = useNavigate();
  const { translate, editableTranslate } = useTranslation();
  const periodId = action?.action_context?.period_id;
  const candidateScope = action?.action_context?.candidate_scope;
  const invalidContextMessage = translate(
    'action.detail.assign_to_period.invalid_context',
    'Die Zielperiode oder der Kandidatenbereich fehlt.'
  );

  const [employees, setEmployees] = useState(null);
  const [loadingPool, setLoadingPool] = useState(true);
  const [poolLoaded, setPoolLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [poolPage, setPoolPage] = useState(1);
  const [poolPerPage, setPoolPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [poolSearch, setPoolSearch] = useState('');
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, perPage: 20, currentPage: 1 });

  useEffect(() => {
    const timer = setTimeout(() => {
      setPoolSearch(searchInput.trim());
      setPoolPage(1);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPoolPage(1);
    setSelected([]);
    setSelectedEmployees({});
    setPage(1);
  }, [periodId, candidateScope, setPage]);

  useEffect(() => {
    let active = true;

    const fetchPool = async () => {
      if (!periodId || !candidateScope) {
        setError(invalidContextMessage);
        setLoadingPool(false);
        setPoolLoaded(true);
        return;
      }

      setLoadingPool(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          period_id: String(periodId),
          scope: candidateScope,
          limit: String(poolPerPage),
          page: String(poolPage),
        });
        if (poolSearch) params.set('search', poolSearch);

        const { data, status } = await api.get(`/employees/assignment-candidates?${params.toString()}`);
        if (!active) return;

        if (status === 200) {
          const list = (data?.data || []).map(item => ({
            hash_id: item.employee_hash_id,
            first_name: item.name,
            last_name: '',
            employee_number: item.staff_id,
          }));
          const meta = data?.meta || {};
          setEmployees(list);
          if (!poolSearch) setFilterCount(meta.total ?? list.length);
          setPagination({
            total: meta.total ?? list.length,
            totalPages: meta.last_page ?? 1,
            perPage: meta.per_page ?? poolPerPage,
            currentPage: meta.current_page ?? poolPage,
          });
        }
      } catch (err) {
        if (!active) return;
        setError(errorMessage(err, 'fetch assignment candidates'));
        Sentry.captureException(err);
      } finally {
        if (active) {
          setLoadingPool(false);
          setPoolLoaded(true);
        }
      }
    };

    fetchPool();

    return () => { active = false; };
  }, [candidateScope, invalidContextMessage, periodId, poolPage, poolPerPage, poolSearch, setFilterCount]);

  const handleApply = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const { status } = await api.post('/employees/period-assignments/assign', {
        period_id: periodId,
        employee_hash_ids: selected,
      });
      if (status === 200) {
        toast.success(translate('action.detail.assign_to_period.success', 'Beschäftigte wurden der Periode zugewiesen.'));
        navigate('/settings/actions');
      }
    } catch (err) {
      setError(errorMessage(err, 'assign employees to period'));
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
    }
  };

  const paginationProps = {
    search: searchInput,
    onSearchChange: setSearchInput,
    total: pagination.total,
    loadingPool,
    page: pagination.currentPage,
    perPage: pagination.perPage,
    totalPages: pagination.totalPages,
    totalRows: pagination.total,
    setPage: setPoolPage,
    setPerPage: value => {
      setPoolPerPage(value);
      setPoolPage(1);
    },
  };

  return <div className="mt-3">
    <hr className="mt-2 mb-4" />
    {error && <div className="mb-3"><ErrorAlert error={error} onClose={() => setError(null)} isHtml={true} /></div>}

    {!poolLoaded ? <div className="text-center p-4" data-testid="loading">
      <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
    </div> : filterCount === 0 ? <>
      <div className="alert alert-warning">
        {editableTranslate('action.detail.assign_to_period.empty', 'Keine berechtigten Beschäftigten für diese Zuweisung gefunden.')}
      </div>
      <div className="settings-sticky-footer"><Button as={Link} data-testid="back-button" variant="secondary" to="/settings/actions">{editableTranslate('button.back', 'Zurück')}</Button></div>
    </> : <>
      {page === 1 && <>
        <div className="mb-5">
          <div className="font-weight-bold">{editableTranslate('action.detail.employees', 'Beschäftigte')}</div>
          <hr className="my-2" />
          <div className="mb-3">{editableTranslate('action.detail.select_employees', 'Wählen Sie die Beschäftigten aus, auf die die Änderungen angewendet werden sollen.')}</div>
          <EmployeeSelector
            employees={employees || []}
            selected={selected}
            setSelected={setSelected}
            onSelectionChange={(items, checked) => setSelectedEmployees(previous => applySelectionChange(previous, items, checked))}
            {...paginationProps}
          />
        </div>
        <div className="mb-4">
          <div className="font-weight-bold">{editableTranslate('action.detail.assign_to_period.title', 'Beschäftigte der Periode zuweisen')}</div>
          <hr className="my-2" />
          <div>{editableTranslate(
            `action.detail.assign_to_period.scope.${candidateScope}`,
            candidateScope === 'previously_in_module' ? 'Bereits früher diesem Modul zugeordnet' : 'Neu in diesem Modul'
          )}</div>
        </div>
        <div className="settings-sticky-footer mt-3">
          <Button as={Link} data-testid="back-button" variant="secondary" className="mr-2" to="/settings/actions">{editableTranslate('button.back', 'Zurück')}</Button>
          <Button variant="primary" onClick={() => setPage(2)} disabled={selected.length === 0} data-testid="next-button">
            {editableTranslate('button.next', 'Weiter')}
          </Button>
        </div>
      </>}

      {page === 2 && <>
        <ActionChangePreview
          whatItems={[{ label: editableTranslate('action.detail.assign_to_period.title', 'Beschäftigte der Periode zuweisen') }]}
          whoList={buildWhoList(selected, selectedEmployees)}
        />
        <div className="settings-sticky-footer d-flex mt-3">
          <Button variant="secondary" onClick={() => setPage(1)} disabled={submitting} data-testid="back-button" className="mr-2">{editableTranslate('button.back', 'Zurück')}</Button>
          <Button variant="primary" onClick={handleApply} loading={submitting} disabled={selected.length === 0} data-testid="proceed-button">{editableTranslate('button.proceed', 'Fortfahren')}</Button>
        </div>
      </>}
    </>}
  </div>;
}
